/**
 * Narrowing the strategy list — ROADMAP 5.19.
 *
 * Pure, so the counts on the pills and the cards below them cannot disagree:
 * both come from this file, and a filter that said "BULLISH 2" over three
 * cards would be a bug nobody could see in a screenshot.
 */

import type { StrategyDto } from '@/types/strategy'
import { legLabel } from './strategyFacts'

/** The pill meaning "do not narrow by bias". Not a bias value. */
export const ALL_BIASES = 'ALL' as const

export type BiasFilter = string | typeof ALL_BIASES

export interface StrategyQuery {
  query: string
  bias: BiasFilter
}

export const EMPTY_QUERY: StrategyQuery = { query: '', bias: ALL_BIASES }

/**
 * Everything one strategy can be found by, lowercased.
 *
 * THE LEGS ARE SEARCHABLE TEXT. A trader looking for "what do I have that
 * sells a put" is asking about structure, not about names, and the names are
 * theirs to change anyway (5.19) — so searching only them would make the box
 * useless on exactly the account that renamed everything.
 *
 * SO ARE `construction` AND `context`, THOUGH THE CARD NO LONGER PRINTS
 * THEM. They were two rows of prose that restated the chips and then
 * truncated, so they came off the card; they lose nothing as index terms.
 * "range-bound" and "higher-strike" still find their strategies, which is
 * the cheap half of what the lines were doing and the half that worked.
 */
function haystack(s: StrategyDto): string {
  return [s.name, s.construction, s.context, s.templateKey, s.bias, ...s.legs.map(legLabel)]
    .join(' ')
    .toLowerCase()
}

export function filterStrategies(items: StrategyDto[], q: StrategyQuery): StrategyDto[] {
  const needle = q.query.trim().toLowerCase()
  return items.filter((s) => {
    if (q.bias !== ALL_BIASES && s.bias !== q.bias) return false
    return needle === '' || haystack(s).includes(needle)
  })
}

/**
 * How many strategies each pill would show.
 *
 * COUNTED AFTER THE SEARCH, BEFORE THE BIAS. A pill has to say how many
 * matches IT holds, so narrowing by bias first would make every pill but the
 * selected one read zero — the counts would vanish the moment they became
 * useful for choosing where to look next.
 */
export function biasCounts(items: StrategyDto[], query: string): Map<string, number> {
  const searched = filterStrategies(items, { query, bias: ALL_BIASES })
  const out = new Map<string, number>()
  for (const s of searched) out.set(s.bias, (out.get(s.bias) ?? 0) + 1)
  out.set(ALL_BIASES, searched.length)
  return out
}
